import React, { useState, useEffect, useRef } from "react"
import { useParams } from "react-router-dom"
import {
  Row,
  Col,
  Card,
  CardBody,
  CardTitle,
  CardText,
  Spinner,
  CardImg,
  Input,
  FormGroup,
  Label,
  Button,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  CardHeader, // Import CardHeader for the Categories heading
  Badge,
  ModalFooter,
  ModalBody,
  ModalHeader,
  Modal,
} from "reactstrap"
import axios from "axios"
import classnames from "classnames"
import config from "config/config"
import yelpIcon from "../../assets/images/icons/yelp.svg"
import googleIcon from "../../assets/images/icons/google.png"
import healthGradeIcon from "../../assets/images/icons/healthgrade.png"
import "./index.css" // Assuming you have a CSS file for custom styles
import GoogleReviewModal from "components/GoogleReviewModal/GoogleReviewModal"
import SnackbarComponent from "components/SnackBar"
import { useSelector } from "react-redux"

const SurveyForm = () => {
  const { campaignId, organizationId, distributionId, surveyId, customerId } =
    useParams()
  const [customerSurveyAssignment, setCustomerSurveyAssignment] = useState(null)
  const [organization, setOrganization] = useState(null)
  const [reviewRedirectionModalOpen, setReviewRedirectionModalOpen] =
    useState(false)
  const FALLBACK_PLACE_ID = "ChIJYyHdmkT92oAR0KLE-JwZcwM"
  const [googleReviewModalOpen, setGoogleReviewModalOpen] = useState(false)
  const [googleReviewUrl, setGoogleReviewUrl] = useState(null)
  const [reviewText, setReviewText] = useState("")
  const [survey, setSurvey] = useState(null)
  const [facebookReviewUrl, setFacebookReviewUrl] = useState(null)
  const [yelpReviewUrl, setYelpReviewUrl] = useState(null)
  const [responses, setResponses] = useState({})
  const [loading, setLoading] = useState(true)
  const [customer, setCustomer] = useState(true)
  const [activeTab, setActiveTab] = useState(0) // Track the active tab index
  const [submitted, setSubmitted] = useState(false)
  const [available, setAvailable] = useState(true)
  const [copySuccess, setCopySuccess] = useState(null)
  const mode = useSelector(state => state.Layout.layoutMode)
  const hiddenTextareaRef = useRef(null)

  const toggleReviewRedirectionModal = () =>
    setReviewRedirectionModalOpen(!reviewRedirectionModalOpen)

  const markSurveyOpened = async () => {
    try {
      await axios.put(
        `${config.backendUrl}/survey/markSurveyOpened/${distributionId}/${surveyId}/${customerId}`,
      )
    } catch (err) {
      console.log(err)
    }
  }

  const getGoogleReviewUrl = org => {
    const pid = org?.googlePlaceId
    if (pid) return `https://search.google.com/local/writereview?placeid=${pid}`
    return org?.GoogleIntegration?.profileUrl || null
  }

  const fetchOrganization = async () => {
    try {
      const response = await axios.get(
        `${config.backendUrl}/organization/getOrganization?orgId=${organizationId}`,
      )
      const org = response.data.organization
      setOrganization({
        ...org,
        // Normalize integrations to the singular keys used throughout the UI
        FacebookIntegration:
          org.FacebookIntegration || org.FacebookIntegrations?.[0] || null,
        YelpIntegration: org.YelpIntegration || org.YelpIntegrations?.[0] || null,
        HealthgradeIntegration:
          org.HealthgradeIntegration || org.HealthgradeIntegrations?.[0] || null,
      })
      setLoading(false) // Organization data loaded
    } catch (err) {
      setLoading(false) // Stop loading in case of error
      console.log(err)
    }
  }

  const fetchCustomerSurveyAssignment = async () => {
    try {
      const response = await axios.get(
        `${config.backendUrl}/survey/customerDistributionStatus/${distributionId}/${surveyId}/${customerId}`,
      )
      setCustomerSurveyAssignment(response.data.assignment)
      if (response.data.assignment.Opened === false) {
        markSurveyOpened()
      }
      if (response.data.assignment.Responded) {
        setSubmitted(true)
      }
    } catch (err) {
      console.log(err)
    }
  }

  const fetchSurvey = async () => {
    try {
      const response = await axios.get(
        `${config.backendUrl}/survey/${surveyId}`,
      )
      setSurvey(response.data.survey)
    } catch (err) {
      console.log(err)
    }
  }

  const handleInputChange = (categoryIndex, questionIndex, value) => {
    const key = `category${categoryIndex}_question${questionIndex}`
    setResponses({ ...responses, [key]: value })
  }

  const fetchFacebookPageUrl = async () => {
    try {
      const response = await axios.get(
        `https://graph.facebook.com/v20.0/${organization.FacebookIntegration.pageId}?fields=name&access_token=${organization.FacebookIntegration.pageToken}`,
      )

      const pageName = response.data.name.toLowerCase().replace(/\s+/g, "")

      const facebookReviewUrl = `https://www.facebook.com/${pageName}/reviews/`

      // Set the URL
      setFacebookReviewUrl(facebookReviewUrl)
    } catch (err) {
      console.log(err)
    }
  }

  const copyReviewTextToClipboard = async value => {
    if (!value) return false
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(value)
        return true
      }
    } catch {}
    try {
      if (hiddenTextareaRef.current) {
        hiddenTextareaRef.current.value = value
        hiddenTextareaRef.current.select()
        return document.execCommand("copy")
      }
    } catch {}
    return false
  }

  const handlePlatformClick = async targetUrl => {
    if (!targetUrl) return
    const copied = await copyReviewTextToClipboard(reviewText)
    if (copied) {
      setCopySuccess("Text has been automatically copied.")
      setTimeout(() => setCopySuccess(null), 2000)
    }
    setTimeout(() => {
      window.open(targetUrl, "_blank", "noopener,noreferrer")
    }, 300)
  }

  const handleSubmit = async () => {
    // debugger
    // Prepare the responses in the desired format
    const formattedResponses = {
      surveyId: surveyId, // This should be the current survey ID
      customerId: customerId, // Make sure this is the correct customer ID
      distributionId: distributionId,
      responses: Object.keys(responses).map(key => {
        const [categoryIndex, questionIndex] = key.match(/\d+/g)
        const questionId =
          survey.SurveyCategories[categoryIndex].SurveyQuestions[questionIndex]
            .id
        return {
          questionId: questionId,
          responseValue: responses[key], // Use the response value directly
        }
      }),
    }

    console.log("hello", formattedResponses)

    try {
      const response = await axios.post(
        `${config.backendUrl}/survey/submitResponse`,
        formattedResponses,
      )
      console.log("Survey submitted successfully", response.data)
      setReviewText(response.data?.reviewText || "")
      setSubmitted(true)

      if (response.data.score) {
        const score =
          typeof response.data?.score === "string"
            ? parseFloat(response.data.score)
            : response.data?.score

        if (Number.isFinite(score) && score > survey.threshold) {
          console.log("Score greater than threshold")
          let nextGoogleUrl = null
          if (customerSurveyAssignment.for === "org") {
            nextGoogleUrl = getGoogleReviewUrl(organization)
          } else {
            console.log("inside doctor for")
            const doctorResponse = await axios.get(
              `${config.backendUrl}/doctors/${customerSurveyAssignment.doctorId}`,
            )

            console.log("Doctor", doctorResponse)

            nextGoogleUrl = doctorResponse.data.doctor.googleReviewLink
            console.log("URL", nextGoogleUrl)
          }
          setGoogleReviewUrl(nextGoogleUrl || null)
          if (organization?.FacebookIntegration) {
            await fetchFacebookPageUrl()
          }
          const hasAnyReviewLink =
            nextGoogleUrl ||
            organization?.FacebookIntegration ||
            organization?.YelpIntegration ||
            organization?.HealthgradeIntegration
          if (hasAnyReviewLink) {
            setReviewRedirectionModalOpen(true)
            setGoogleReviewModalOpen(false) // avoid stacking
          }
        }
      }
      // fetchFacebookPageUrl()
      // toggleReviewRedirectionModal()
    } catch (err) {
      console.log("Error submitting survey", err)
    }
  }

  const fetchCampaign = async () => {
    try {
      const campaignResponse = await axios.get(
        `${config.backendUrl}/survey/getCampaign?distributionId=${campaignId}`,
      )

      if (campaignResponse.data.campaign.status !== "active") {
        console.log("Active")
        setAvailable(false)
      }

      console.log("Hellohhhh", campaignResponse)
    } catch (err) {
      console.log(err)
    }
  }

  useEffect(() => {
    fetchCampaign()
    fetchCustomerSurveyAssignment()
    fetchOrganization()
    fetchSurvey()
  }, [])

  return (
    <div className="p-4">
      <Row className="justify-content-center">
        <Col xl={12} md={12}>
          <Card className="shadow-lg border-0 br">
            <CardBody className="text-center">
              {loading ? (
                <Spinner color="primary" />
              ) : (
                <>
                  {/* Display Organization Logo */}
                  {organization && organization.logo && (
                    <CardImg
                      top
                      src={organization.logo}
                      alt={`${organization.name} Logo`}
                      style={{
                        maxWidth: "150px",
                        marginBottom: "20px",
                        borderRadius: "10px",
                      }}
                    />
                  )}

                  {/* Display Organization Name */}
                  <CardTitle
                    tag="h4"
                    className="mb-3 font-weight-bold"
                    onClick={() => {
                      console.log("Hello", survey)
                    }}
                  >
                    {organization ? organization.name : "Organization Name"}
                  </CardTitle>

                  {/* Welcome Message */}
                  <CardText className="lead">
                    {organization
                      ? `Welcome to ${organization.name}. We value your feedback and encourage you to take your time in completing this survey.`
                      : "Loading organization details..."}
                  </CardText>

                  {/* Organization Description */}
                  <CardText className="text-muted">
                    {organization && organization.description
                      ? organization.description
                      : "We strive to enhance our services with your valuable insights. Feel free to reach out for any queries or support."}
                  </CardText>
                </>
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>
      {submitted ? (
        <Row>
          <Col xl={12}>
            <Card className="text-center mt-4 br">
              <CardBody>
                <CardTitle tag="h5" className="font-weight-bold">
                  Survey Submitted
                </CardTitle>
                <CardText>Thank you for your feedback!</CardText>
              </CardBody>
            </Card>
          </Col>
        </Row>
      ) : !available ? (
        <Row>
          <Col xl={12}>
            <Card className="text-center mt-4 br">
              <CardBody>
                <CardTitle tag="h5" className="font-weight-bold">
                  Survey Unavailable
                </CardTitle>
                <CardText>The survey is currently unavailable.</CardText>
              </CardBody>
            </Card>
          </Col>
        </Row>
      ) : (
        <Row className="justify-content-center">
          <Col xl={12} md={12}>
            <Card className="shadow-lg border-0 br" style={{ height: "100%" }}>
              <CardBody>
                {loading ? (
                  <Spinner color="primary" />
                ) : (
                  <>
                    {/* Display Survey Title and Description */}
                    {survey && survey.title && (
                      <CardTitle tag="h4" className="text-center">
                        {survey.title}
                      </CardTitle>
                    )}
                    {survey && survey.description && (
                      <CardText className="text-muted text-center">
                        {survey.description}
                      </CardText>
                    )}

                    {/* Display Survey Questions, Categories as Sections */}
                    {survey &&
                      survey.SurveyCategories &&
                      survey.SurveyCategories.map((category, categoryIndex) => (
                        <div key={categoryIndex} className="text-center">
                          {category.SurveyQuestions.map(
                            (question, questionIndex) => (
                              <FormGroup
                                key={questionIndex}
                                className="d-flex flex-column align-items-center"
                              >
                                <Label className="text-center">
                                  {question.questionText}{" "}
                                  <Badge pill>{category.name}</Badge>
                                </Label>
                                {question.questionType ===
                                  "multiple choice" && (
                                  <div className="d-flex justify-content-center flex-wrap">
                                    {question.options.map(
                                      (option, optionIndex) => (
                                        <FormGroup
                                          check
                                          inline
                                          key={optionIndex}
                                          className="mx-2"
                                        >
                                          <Label check className="custom-radio">
                                            <input
                                              type="radio"
                                              style={{
                                                backgroundColor: "#2990DF",
                                              }}
                                              name={`question${categoryIndex}_${questionIndex}`}
                                              value={option}
                                              onChange={() =>
                                                handleInputChange(
                                                  categoryIndex,
                                                  questionIndex,
                                                  option,
                                                )
                                              }
                                            />
                                            <span className="custom-radio-btn no-border">
                                              {option}
                                            </span>
                                          </Label>
                                        </FormGroup>
                                      ),
                                    )}
                                  </div>
                                )}
                                {question.questionType === "text" && (
                                  <input
                                    type="text"
                                    style={{
                                      padding: "10px 8px",
                                      maxWidth: "300px",
                                      borderRadius: "5px",
                                    }}
                                    className="input-field-custom"
                                    placeholder="Enter your answer"
                                    onChange={e =>
                                      handleInputChange(
                                        categoryIndex,
                                        questionIndex,
                                        e.target.value,
                                      )
                                    }
                                  />
                                )}
                                {question.questionType === "rating" &&
                                  (() => {
                                    const key = `category${categoryIndex}_question${questionIndex}`
                                    const currentValue = responses[key] ?? 0

                                    return (
                                      <div className="rating-slider-container">
                                        <span className="slider-label">0</span>
                                        <input
                                          type="range"
                                          min="0"
                                          max="5"
                                          step="1"
                                          value={currentValue}
                                          onChange={e =>
                                            handleInputChange(
                                              categoryIndex,
                                              questionIndex,
                                              Number(e.target.value),
                                            )
                                          }
                                          className="rating-slider-custom"
                                          style={{
                                            background: `linear-gradient(to right,
            #2990df ${(currentValue / 5) * 100}%,
            #cfe7fb ${(currentValue / 5) * 100}%)`,
                                          }}
                                        />
                                        <span className="slider-label">5</span>
                                      </div>
                                    )
                                  })()}

                                <br></br>
                              </FormGroup>
                            ),
                          )}
                        </div>
                      ))}

                    <div style={{ height: "80px" }} />
                  </>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      )}
      <Modal
        isOpen={reviewRedirectionModalOpen}
        toggle={toggleReviewRedirectionModal}
        size="lg"
      >
        <ModalHeader toggle={toggleReviewRedirectionModal}>
          Please give us a review on the platforms below. We appreciate it!
        </ModalHeader>
        <ModalBody>
          <CardText className="text-muted text-center">
            Suggested review text (auto-copied when you choose a platform):
          </CardText>
          <Input
            type="textarea"
            rows={3}
            value={reviewText}
            readOnly
            className="mb-3"
            placeholder="Preset review text will appear here."
          />
          <CardText className="text-center">
            {reviewText ? reviewText : "Preset review text will appear here."}
          </CardText>
          <Row className="text-center justify-content-center">
            {/* Google Integration */}
            {googleReviewUrl && (
              <Col xs={6} md={4} className="mb-4">
                <button
                  type="button"
                  className="btn btn-link p-0 border-0"
                  onClick={() => handlePlatformClick(googleReviewUrl)}
                >
                  <div className="review-platform">
                    <img
                      src={googleIcon}
                      alt="Google"
                      style={{
                        height: "50px",
                        objectFit: "contain",
                      }}
                    />
                    <p style={{ marginTop: "10px", color: "#4285F4" }}>
                      Google
                    </p>
                  </div>
                </button>
              </Col>
            )}
            {/* Facebook Integration */}
            {organization?.FacebookIntegration && facebookReviewUrl && (
              <Col xs={6} md={4} className="mb-4">
                <button
                  type="button"
                  className="btn btn-link p-0 border-0"
                  onClick={() => handlePlatformClick(facebookReviewUrl)}
                >
                  <div className="review-platform">
                    <i
                      className="mdi mdi-facebook"
                      style={{
                        fontSize: "50px",
                        color: "#3b5998",
                      }}
                    ></i>
                    <p style={{ marginTop: "10px", color: "#3b5998" }}>
                      Facebook
                    </p>
                  </div>
                </button>
              </Col>
            )}

            {/* Yelp Integration */}
            {organization?.YelpIntegration && (
              <Col xs={6} md={4} className="mb-4">
                <button
                  type="button"
                  className="btn btn-link p-0 border-0"
                  onClick={() =>
                    handlePlatformClick(organization?.YelpIntegration.profileUrl)
                  }
                >
                  <div className="review-platform">
                    <img
                      src={yelpIcon}
                      alt="Yelp"
                      style={{
                        height: "50px",
                      }}
                    />
                    <p style={{ marginTop: "10px", color: "#d32323" }}>Yelp</p>
                  </div>
                </button>
              </Col>
            )}

            {/* Healthgrades Integration */}
            {organization?.HealthgradeIntegration && (
              <Col xs={6} md={4} className="mb-4">
                <button
                  type="button"
                  className="btn btn-link p-0 border-0"
                  onClick={() =>
                    handlePlatformClick(
                      organization?.HealthgradeIntegration.profileUrl,
                    )
                  }
                >
                  <div className="review-platform">
                    <img
                      src={healthGradeIcon}
                      alt="Healthgrades"
                      style={{
                        height: "50px",
                      }}
                    />
                    <p style={{ marginTop: "10px", color: "#007bb5" }}>
                      Healthgrades
                    </p>
                  </div>
                </button>
              </Col>
            )}
          </Row>
        </ModalBody>
        <ModalFooter className="d-flex justify-content-center">
          <Button color="secondary" onClick={toggleReviewRedirectionModal}>
            Cancel
          </Button>
        </ModalFooter>
      </Modal>
      <GoogleReviewModal
        open={googleReviewModalOpen}
        toggle={setGoogleReviewModalOpen}
        url={googleReviewUrl}
        presetText={reviewText || ""}
        // openMode="popup" // optional: 'popup' | 'newtab' | 'sametab'
      />
      <textarea
        ref={hiddenTextareaRef}
        style={{ position: "absolute", left: -9999, top: -9999, opacity: 0 }}
        readOnly
      />
      <SnackbarComponent
        err={null}
        setErr={() => {}}
        success={copySuccess}
        setSuccess={setCopySuccess}
      />

      {/* Floating progress + submit bar */}
      {!submitted && !loading && survey && (
        (() => {
          const totalQuestions = survey.SurveyCategories?.reduce(
            (sum, cat) => sum + (cat.SurveyQuestions?.length || 0), 0
          ) || 0
          const answeredQuestions = Object.keys(responses).filter(
            k => responses[k] !== "" && responses[k] !== null && responses[k] !== undefined
          ).length
          const allAnswered = answeredQuestions === totalQuestions && totalQuestions > 0
          const progressPct = totalQuestions > 0 ? Math.round((answeredQuestions / totalQuestions) * 100) : 0

          return (
            <div style={{
              position: "fixed",
              bottom: "32px",
              left: "50%",
              transform: "translateX(-50%)",
              zIndex: 1050,
              backgroundColor: "#fff",
              border: "1px solid #e5e7eb",
              borderRadius: "14px",
              boxShadow: "0 8px 32px rgba(0,0,0,0.18)",
              padding: "14px 24px",
              display: "flex",
              alignItems: "center",
              gap: "20px",
              minWidth: "360px",
              fontFamily: "Inter, sans-serif",
            }}>
              {/* Progress info */}
              <div style={{ flex: 1 }}>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "6px" }}>
                  <span style={{ fontSize: "13px", fontWeight: 600, color: "#111827" }}>
                    {answeredQuestions} of {totalQuestions} answered
                  </span>
                  <span style={{ fontSize: "13px", fontWeight: 600, color: "#2990DF" }}>
                    {progressPct}%
                  </span>
                </div>
                <div style={{ height: "6px", backgroundColor: "#e5e7eb", borderRadius: "999px", overflow: "hidden" }}>
                  <div style={{
                    height: "100%",
                    width: `${progressPct}%`,
                    backgroundColor: allAnswered ? "#22c55e" : "#2990DF",
                    borderRadius: "999px",
                    transition: "width 0.3s ease",
                  }} />
                </div>
              </div>

              {/* Submit button */}
              <button
                onClick={allAnswered ? handleSubmit : undefined}
                style={{
                  padding: "10px 22px",
                  borderRadius: "8px",
                  border: "none",
                  backgroundColor: allAnswered ? "#22c55e" : "#d1d5db",
                  color: allAnswered ? "#fff" : "#9ca3af",
                  fontSize: "14px",
                  fontWeight: 600,
                  cursor: allAnswered ? "pointer" : "not-allowed",
                  fontFamily: "Inter, sans-serif",
                  whiteSpace: "nowrap",
                  transition: "all 0.2s",
                }}
              >
                {allAnswered ? "Submit Survey" : "Answer all questions"}
              </button>
            </div>
          )
        })()
      )}
    </div>
  )
}

export default SurveyForm
